import React, { useEffect, useRef, useState } from "react";
import { X, Phone, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const GOOGLE_AUTH_URL = "https://auth.emergentagent.com/";

function sanitizePhone(raw) {
  const s = (raw || "").replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+")) return s;
  if (s.length === 10) return "+91" + s; // India default
  return "+" + s;
}

export default function LoginModal({ open, onClose, onLoggedIn }) {
  const [step, setStep] = useState("choose"); // choose | phone | code
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("choose"); setPhone(""); setCode(""); setResendIn(0);
    }
  }, [open]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    if (step === "code") setTimeout(() => codeRef.current?.focus(), 50);
  }, [step]);

  if (!open) return null;

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendOtp = async () => {
    const p = sanitizePhone(phone);
    if (!/^\+[1-9]\d{7,14}$/.test(p)) {
      toast.error("Enter a valid phone number with country code (e.g. +91 98765 43210)");
      return;
    }
    setPhone(p);
    setSending(true);
    try {
      await api.post("/auth/otp/start", { phone: p });
      toast.success("OTP sent to " + p);
      setStep("code");
      setResendIn(30);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send OTP";
      toast.error(typeof msg === "string" ? msg : "Could not send OTP");
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{4,8}$/.test(code)) {
      toast.error("Enter the 6-digit code from SMS");
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post("/auth/otp/verify", { phone, code });
      toast.success("Signed in");
      onLoggedIn?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Verification failed";
      toast.error(typeof msg === "string" ? msg : "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div
      data-testid="login-modal-backdrop"
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        data-testid="login-modal"
        className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl border border-slate-200 relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-3 right-3 z-10">
          <button
            data-testid="login-modal-close"
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-8 pb-2">
          {step !== "choose" && (
            <button
              data-testid="login-modal-back"
              onClick={() => setStep(step === "code" ? "phone" : "choose")}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#0A2540] mb-3"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}
          <h2 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            {step === "choose" && "Continue to Advoset"}
            {step === "phone" && "Enter your phone"}
            {step === "code" && "Verify your number"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {step === "choose" && "New users will be automatically registered."}
            {step === "phone" && "We'll text you a 6-digit code. Standard SMS rates may apply."}
            {step === "code" && `We sent a code to ${phone}.`}
          </p>
        </div>

        <div className="px-6 py-5 space-y-3">
          {step === "choose" && (
            <>
              <button
                data-testid="login-google-btn"
                onClick={googleLogin}
                className="w-full h-12 rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 font-semibold text-sm text-slate-800 inline-flex items-center justify-center gap-3 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.9 6.1 29.7 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.9 6.1 29.7 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.6c-2 1.5-4.6 2.4-7.7 2.4-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6.6 5.6C40.9 35.7 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                Continue with Google
              </button>
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">or</span></div>
              </div>
              <button
                data-testid="login-phone-btn"
                onClick={() => setStep("phone")}
                className="w-full h-12 rounded-full adv-btn-primary font-semibold text-sm text-white inline-flex items-center justify-center gap-2"
              >
                <Phone size={16} /> Continue with phone number
              </button>
              <p className="text-[11px] text-slate-500 text-center pt-2">
                By continuing, you agree to our Terms and Privacy Policy.
              </p>
            </>
          )}

          {step === "phone" && (
            <>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Phone number</label>
              <div className="flex items-center gap-2 rounded-2xl border-2 border-slate-200 focus-within:border-[#0A2540] transition-colors px-4 py-3">
                <span className="text-lg font-semibold text-slate-500">🇮🇳</span>
                <input
                  data-testid="login-phone-input"
                  type="tel"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !sending && sendOtp()}
                  placeholder="+91 98765 43210"
                  className="flex-1 outline-none text-base font-medium text-[#0A2540] placeholder:text-slate-300"
                />
              </div>
              <button
                data-testid="login-send-otp-btn"
                onClick={sendOtp}
                disabled={sending}
                className="w-full h-12 rounded-full adv-btn-gold font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                {sending ? "Sending…" : "Send OTP"}
              </button>
              <p className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 justify-center w-full pt-1">
                <ShieldCheck size={12} className="text-emerald-600" /> Your number stays private. Never shared.
              </p>
            </>
          )}

          {step === "code" && (
            <>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">6-digit code</label>
              <input
                ref={codeRef}
                data-testid="login-otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && !verifying && verifyOtp()}
                className="w-full text-center text-2xl tracking-[0.5em] font-bold rounded-2xl border-2 border-slate-200 focus:border-[#0A2540] outline-none py-4 text-[#0A2540]"
                placeholder="••••••"
              />
              <button
                data-testid="login-verify-btn"
                onClick={verifyOtp}
                disabled={verifying}
                className="w-full h-12 rounded-full adv-btn-gold font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {verifying ? <Loader2 size={16} className="animate-spin" /> : null}
                {verifying ? "Verifying…" : "Verify & continue"}
              </button>
              <div className="text-xs text-slate-500 text-center">
                Didn't get it?{" "}
                {resendIn > 0 ? (
                  <span className="text-slate-400">Resend in {resendIn}s</span>
                ) : (
                  <button
                    data-testid="login-resend-btn"
                    onClick={sendOtp}
                    className="font-semibold text-[#0A2540] hover:underline"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
